import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, ExternalLink, Star, Users, Globe, DollarSign, CheckCircle2, Search as SearchIcon, Building2, AlertCircle, Landmark, Handshake, Layers } from 'lucide-react';
import { formatPayDisplay } from '../utils/payDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYER_TYPE_STYLES = {
  direct_employer: { bg: 'bg-[#047857]/10', text: 'text-[#047857]', icon: Building2 },
  staffing_agency: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Landmark },
  platform_contractor: { bg: 'bg-[#D97757]/10', text: 'text-[#D97757]', icon: Handshake },
  job_board: { bg: 'bg-blue-50', text: 'text-blue-600', icon: Layers },
};

function EmployerTypeBadge({ type, label }) {
  const style = EMPLOYER_TYPE_STYLES[type] || EMPLOYER_TYPE_STYLES.direct_employer;
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${style.bg} ${style.text}`} data-testid={`badge-${type}`}>
      <Icon size={10} strokeWidth={1.5} />{label}
    </span>
  );
}

function LiveJobsBadge({ count, platformName }) {
  const navigate = useNavigate();
  if (count === 0) return null;
  return (
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/jobs?search=${encodeURIComponent(platformName)}`); }}
      className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full font-semibold bg-[#047857] text-white hover:bg-[#065F46] transition-colors"
      data-testid={`live-jobs-badge-${platformName}`}
    >
      <Briefcase size={10} strokeWidth={2} />{count} live {count === 1 ? 'opening' : 'openings'}
    </button>
  );
}

function EmptyStateCard({ platform }) {
  return (
    <div className="mt-3 bg-[#F4F1E8]/50 rounded-xl p-4 border border-[#E5E0D8]/50" data-testid={`empty-state-${platform.id}`}>
      <div className="flex items-start gap-3">
        <AlertCircle size={16} className="text-[#1C2A24]/30 mt-0.5 shrink-0" strokeWidth={1.5} />
        <div className="flex-1">
          <p className="text-sm text-[#1C2A24]/50">No live openings synced right now</p>
          <p className="text-xs text-[#1C2A24]/30 mt-1">Check back soon — new positions are synced regularly.</p>
        </div>
        <a href={platform.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
          <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-3 border-[#E5E0D8] text-[#047857] hover:bg-[#047857]/5" data-testid={`browse-site-${platform.id}`}>
            Browse official site <ExternalLink size={10} className="ml-1" strokeWidth={1.5} />
          </Button>
        </a>
      </div>
    </div>
  );
}

export default function WFHPlatforms() {
  const [platforms, setPlatforms] = useState([]);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    axios.get(`${API}/platforms`).then(r => { setPlatforms(r.data.platforms); setLoading(false); }).catch(console.error);
  }, []);

  const categories = ['all', ...new Set(platforms.map(p => p.category))];
  const employerTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'direct_employer', label: 'Direct Employers' },
    { value: 'staffing_agency', label: 'Staffing Agencies' },
    { value: 'platform_contractor', label: 'Contractor Platforms' },
    { value: 'job_board', label: 'Job Boards' },
  ];

  const filtered = platforms.filter(p => {
    if (filter !== 'all' && p.category !== filter) return false;
    if (typeFilter !== 'all' && p.employer_type !== typeFilter) return false;
    if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase()) && !p.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const featured = platforms.filter(p => p.featured);
  const totalLiveJobs = platforms.reduce((sum, p) => sum + (p.live_job_count || 0), 0);
  const platformsWithJobs = platforms.filter(p => p.live_job_count > 0).length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
          <Link to="/jobs" className="text-sm text-[#047857] hover:underline">Back to Jobs</Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>WFH Job Platforms</h1>
          <p className="text-[#1C2A24]/60 mt-2">Curated directory of {platforms.length} platforms that specialize in work-from-home positions.</p>
          {!loading && (
            <div className="flex items-center gap-4 mt-3">
              <span className="text-xs text-[#047857] bg-[#A7F3D0]/20 px-3 py-1 rounded-full font-medium" data-testid="live-jobs-summary">
                {totalLiveJobs} live openings across {platformsWithJobs} platforms
              </span>
            </div>
          )}
        </div>

        {/* Featured Platforms Banner */}
        {featured.length > 0 && (
          <div className="mb-10" data-testid="featured-platforms">
            <h2 className="text-lg font-medium mb-4 flex items-center gap-2" style={{fontFamily:'Outfit'}}>
              <Star size={16} className="text-[#D97757]" strokeWidth={1.5} /> Top Recommended Platforms
            </h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
              {featured.map(p => (
                <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer"
                  className="bg-white rounded-2xl border border-[#047857]/20 p-4 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300 group" data-testid={`featured-${p.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <EmployerTypeBadge type={p.employer_type} label={p.employer_type_label} />
                    <div className="flex items-center gap-1"><Star size={10} className="text-[#D97757] fill-[#D97757]" /><span className="text-[10px]">{p.rating}</span></div>
                  </div>
                  <h3 className="font-medium text-sm mb-1" style={{fontFamily:'Outfit'}}>{p.name}</h3>
                  <p className="text-[10px] text-[#047857] font-medium">{formatPayDisplay(p.pay_range).text}</p>
                  {p.live_job_count > 0 && (
                    <p className="text-[10px] text-[#047857]/70 mt-1 font-medium">{p.live_job_count} live {p.live_job_count === 1 ? 'opening' : 'openings'}</p>
                  )}
                  <ExternalLink size={12} className="text-[#047857] mt-2 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <SearchIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/30" strokeWidth={1.5} />
          <input
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search platforms by name or description..."
            className="w-full pl-10 pr-4 h-11 rounded-full bg-white border border-[#E5E0D8] text-sm focus:outline-none focus:ring-2 focus:ring-[#047857]/20"
            data-testid="platform-search"
          />
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="platform-filters">
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${filter === cat ? 'bg-[#047857] text-white' : 'bg-[#F4F1E8] text-[#1C2A24]/70 hover:bg-[#E5E0D8]'}`}>
              {cat === 'all' ? 'All Categories' : cat}
            </button>
          ))}
        </div>

        {/* Employer Type Filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="employer-type-filters">
          {employerTypes.map(et => (
            <button key={et.value} onClick={() => setTypeFilter(et.value)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all border ${typeFilter === et.value ? 'bg-[#1C2A24] text-white border-[#1C2A24]' : 'bg-white text-[#1C2A24]/60 border-[#E5E0D8] hover:border-[#1C2A24]/20'}`}>
              {et.label}
            </button>
          ))}
        </div>

        {/* Staffing Agency Notice */}
        {typeFilter === 'staffing_agency' && (
          <div className="bg-amber-50 border border-amber-200/50 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="staffing-notice">
            <Landmark size={16} className="text-amber-600 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-amber-800" style={{fontFamily:'Outfit'}}>About Staffing Agencies</p>
              <p className="text-xs text-amber-700/70 mt-1">Staffing agencies like Robert Half and Kelly Services connect job seekers with positions at partner companies. Roles may be contract, temp-to-hire, or placed at third-party employers.</p>
            </div>
          </div>
        )}

        {/* All Platforms Grid */}
        <div className="space-y-4" data-testid="platforms-list">
          {loading ? [1,2,3].map(i => <div key={i} className="h-32 bg-white rounded-2xl animate-pulse" />) :
            filtered.length === 0 ? (
              <div className="text-center py-16">
                <Globe size={48} className="text-[#E5E0D8] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-lg text-[#1C2A24]/60">No platforms match your filters.</p>
                <button onClick={() => { setFilter('all'); setTypeFilter('all'); setSearchTerm(''); }} className="text-sm text-[#047857] hover:underline mt-2">Clear all filters</button>
              </div>
            ) :
            filtered.map(platform => (
              <div key={platform.id} className="bg-white rounded-3xl border border-[#E5E0D8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(4,120,87,0.06)] transition-all duration-300" data-testid={`platform-${platform.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="text-lg font-medium" style={{fontFamily:'Outfit'}}>{platform.name}</h3>
                      {platform.featured && <span className="text-[10px] bg-[#D97757]/10 text-[#D97757] px-2 py-0.5 rounded-full font-medium">Recommended</span>}
                      <EmployerTypeBadge type={platform.employer_type} label={platform.employer_type_label} />
                      <LiveJobsBadge count={platform.live_job_count} platformName={platform.name} />
                    </div>
                    <p className="text-sm text-[#1C2A24]/70 mb-3 max-w-2xl">{platform.description}</p>

                    {/* Staffing agency note */}
                    {platform.employer_note && (
                      <p className="text-xs text-amber-700/80 bg-amber-50 rounded-lg px-3 py-1.5 mb-3 inline-block" data-testid={`agency-note-${platform.id}`}>
                        {platform.employer_note}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-4 text-xs text-[#1C2A24]/50">
                      <span className="flex items-center gap-1">
                        <DollarSign size={12} strokeWidth={1.5} /> {formatPayDisplay(platform.pay_range).text}
                      </span>
                      <span className="flex items-center gap-1"><Users size={12} strokeWidth={1.5} /> {platform.type}</span>
                      <span className="flex items-center gap-1"><Star size={12} className="text-[#D97757]" strokeWidth={1.5} /> {platform.rating}/5</span>
                    </div>
                    {platform.requirements && (
                      <p className="text-xs text-[#1C2A24]/70 mt-2 flex items-start gap-1">
                        <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-[#047857]" strokeWidth={1.5} /> {platform.requirements}
                      </p>
                    )}

                    {/* Empty state when no live jobs */}
                    {platform.live_job_count === 0 && platform.employer_type !== 'job_board' && (
                      <EmptyStateCard platform={platform} />
                    )}
                  </div>
                  <a href={platform.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                    <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white ml-4" data-testid={`visit-${platform.id}`}>
                      Visit Site <ExternalLink size={14} className="ml-2" strokeWidth={1.5} />
                    </Button>
                  </a>
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}
